import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import CustomDashboardComposer from '../../components/dashboard/custom/CustomDashboardComposer';
import { customDashboardPath } from '../../navigation/dashboards';
import type { TeamDashboard, TeamDashboards as Team } from './homeNumbers';

interface TeamDashboardsProps {
  projectId: number;
  team: Team;
}

// Small round pills under the cards (the owner's mockup of 22 Oct 2026): white, a thin
// border, the cards' hover.
const CHIP = 'inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-[13px] font-medium text-gray-700 transition hover:border-primary-200 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

function DashboardChip({ dashboard, draft }: { dashboard: TeamDashboard; draft?: boolean }): ReactElement {
  return (
    <Link to={customDashboardPath(dashboard.id)} title={dashboard.description ?? dashboard.title} className={CHIP}>
      <span className="min-w-0 max-w-[16rem] truncate">{dashboard.title}</span>
      {draft && (
        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
          Draft
        </span>
      )}
    </Link>
  );
}

/**
 * The dashboards people made by typing what they wanted to see, as one quiet row of
 * pills under the Dashboards tab's cards: published ones first, then drafts, each marked. "Build a
 * dashboard" is offered only to someone the server says may build, and opens the same
 * box the Dashboard has always used. Left out when there is nothing to show and nothing
 * to build.
 */
export default function TeamDashboards({ projectId, team }: TeamDashboardsProps): ReactElement | null {
  const navigate = useNavigate();
  const [composerOpen, setComposerOpen] = useState(false);
  const { canAuthor, published, drafts } = team;
  const built = published.length + drafts.length;
  if (built === 0 && !canAuthor) return null;

  return (
    <div>
      <nav aria-label="Built by your team" className="flex flex-wrap items-center gap-2 text-[13px] text-gray-500">
        {/* Not said over an empty row: with nothing built yet there is only the button. */}
        {built > 0 && <span>Built by your team:</span>}
        {published.map((d) => <DashboardChip key={d.id} dashboard={d} />)}
        {drafts.map((d) => <DashboardChip key={d.id} dashboard={d} draft />)}
        {canAuthor && (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-primary-300 bg-white px-3 py-1 text-[13px] font-semibold text-primary-600 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Build a dashboard
          </button>
        )}
      </nav>

      {composerOpen && (
        <CustomDashboardComposer
          projectId={projectId}
          onClose={() => setComposerOpen(false)}
          onCreated={(dashboard, failures) => {
            setComposerOpen(false);
            if (failures.length > 0) {
              // Partial success is expected by design: the dashboard exists with the
              // blocks that generated. Naming the ones that did not lets the author
              // reword just those instead of wondering what is missing.
              window.alert(
                `Dashboard created, but ${failures.length} block${failures.length === 1 ? '' : 's'} couldn’t be generated:\n\n` +
                  failures.map((f) => `• ${f.title}: ${f.reason}`).join('\n'),
              );
            }
            navigate(customDashboardPath(dashboard.id));
          }}
        />
      )}
    </div>
  );
}
